import React, { useState, useEffect, useMemo } from 'react';
import { X, Table, Copy, Check, Download, Layers, SlidersHorizontal, CheckSquare, Square, ArrowUpDown, MessageCircle } from 'lucide-react';
import {
  generateMarkdownTable,
  generatePlainTextTable,
  generateDiscordTable,
  AVAILABLE_TABLE_COLUMNS
} from '../utils/exportTable';
import { SORT_OPTIONS, formatCategoryLabel } from '../utils/sortUtils';
import { STATUS_OPTIONS, CATEGORY_DEFS, DEFAULT_EXPORT_CATEGORIES } from '../utils/exportFilterOptions';
import DiscordCopyModal from './DiscordCopyModal';

export default function ExportTableModal({
  isOpen,
  onClose,
  protocols = [],
  currentSortOption = 'default'
}) {
  const [status, setStatus] = useState('Active');
  const [categories, setCategories] = useState(() => [...DEFAULT_EXPORT_CATEGORIES]);
  const [sortOption, setSortOption] = useState('default');
  const [format, setFormat] = useState('markdown'); // 'markdown' | 'text' | 'discord'
  const [copiedFormat, setCopiedFormat] = useState(null); // 'markdown' | 'text' | null
  const [isDiscordCopyModalOpen, setIsDiscordCopyModalOpen] = useState(false);

  // State for selected column IDs
  const [selectedColumns, setSelectedColumns] = useState(() => 
    AVAILABLE_TABLE_COLUMNS.filter((c) => c.defaultSelected).map((c) => c.id)
  );

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setStatus('Active');
      setCategories([...DEFAULT_EXPORT_CATEGORIES]);
      setSortOption(currentSortOption || 'default');
      setFormat('markdown');
      setCopiedFormat(null);
      setIsDiscordCopyModalOpen(false);
      setSelectedColumns(AVAILABLE_TABLE_COLUMNS.filter((c) => c.defaultSelected).map((c) => c.id));
    }
  }, [isOpen, currentSortOption]);

  const toggleCategory = (catId) => {
    setCategories((prev) => {
      if (prev.includes(catId)) {
        return prev.filter((c) => c !== catId);
      } else {
        return [...prev, catId];
      }
    });
  };

  const selectAllCategories = () => {
    setCategories([...DEFAULT_EXPORT_CATEGORIES]);
  };

  const isAllSelected = categories.length === DEFAULT_EXPORT_CATEGORIES.length;

  const toggleColumn = (colId) => {
    setSelectedColumns((prev) => {
      if (prev.includes(colId)) {
        if (prev.length <= 1) return prev; // Prevent deselecting all columns
        return prev.filter((id) => id !== colId);
      } else {
        return [...prev, colId];
      }
    });
  };

  const handleSelectPreset = (preset) => {
    switch (preset) {
      case 'all':
        setSelectedColumns(AVAILABLE_TABLE_COLUMNS.map((c) => c.id));
        break;
      case 'compact':
        setSelectedColumns(['name', 'category', 'cycleStatus', 'dosage', 'frequencySchedule']);
        break;
      case 'default':
      default:
        setSelectedColumns(AVAILABLE_TABLE_COLUMNS.filter((c) => c.defaultSelected).map((c) => c.id));
        break;
    }
  };

  const markdownContent = useMemo(() => {
    return generateMarkdownTable(protocols, { status, categories, sortOption, selectedColumns });
  }, [protocols, status, categories, sortOption, selectedColumns]);

  const plainTextContent = useMemo(() => {
    return generatePlainTextTable(protocols, { status, categories, sortOption, selectedColumns });
  }, [protocols, status, categories, sortOption, selectedColumns]);

  const discordContent = useMemo(() => {
    return generateDiscordTable(protocols, { status, categories, sortOption, selectedColumns });
  }, [protocols, status, categories, sortOption, selectedColumns]);

  const currentContent =
    format === 'markdown' ? markdownContent :
    format === 'discord' ? discordContent :
    plainTextContent;

  if (!isOpen) return null;

  const handleCopy = async (type) => {
    const textToCopy =
      type === 'markdown' ? markdownContent :
      type === 'discord' ? discordContent :
      plainTextContent;
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopiedFormat(type);
      setTimeout(() => setCopiedFormat(null), 2500);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };

  const handleDownload = () => {
    const isMd = format === 'markdown';
    const isDiscord = format === 'discord';
    const content = isMd ? markdownContent : isDiscord ? discordContent : plainTextContent;
    const ext = isDiscord ? 'txt' : isMd ? 'md' : 'txt';
    const mime = isMd ? 'text/markdown' : 'text/plain';

    const categoryLabel = formatCategoryLabel(categories);
    const safeCat = categoryLabel.toLowerCase().replace(/&/g, '').replace(/\s+/g, '-').replace(/--+/g, '-');
    const safeStatus = status.toLowerCase();
    const filename = `supps-and-peps-${safeCat}-${safeStatus}-${isDiscord ? 'discord' : 'table'}.${ext}`;

    const blob = new Blob([content], { type: `${mime};charset=utf-8;` });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <>
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-slate-950/80 backdrop-blur-sm animate-fadeIn overflow-y-auto touch-pan-y">
      <div className="bg-slate-900 border border-slate-800 rounded-2xl max-w-4xl w-full flex flex-col max-h-[92vh] sm:max-h-[88vh] shadow-2xl relative my-auto overflow-hidden">
        
        {/* Sticky Header */}
        <div className="flex items-center justify-between p-4 sm:p-5 border-b border-slate-800 shrink-0 bg-slate-900">
          <div className="flex items-center space-x-3">
            <div className="p-2.5 rounded-xl bg-sky-500/10 text-sky-400 border border-sky-500/20">
              <Table className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-bold text-white">Export Stack as Table</h2>
              <p className="text-[11px] sm:text-xs text-slate-400">
                Customize status, multiple categories, sort order, and columns for Markdown / Plain Text / Discord Chat tables
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-white p-2 rounded-lg hover:bg-slate-800 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable Body */}
        <div className="p-4 sm:p-6 overflow-y-auto flex-1 touch-pan-y space-y-4 text-xs">
          
          {/* Options & Controls Container */}
          <div className="bg-slate-950/60 p-3.5 sm:p-4 rounded-xl border border-slate-800 space-y-3.5">
            
            {/* Top Row: Status Filter & Sorting Order */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Status Option */}
              <div>
                <label className="block text-[11px] font-semibold text-slate-400 mb-1">Status Filter</label>
                <div className="flex rounded-lg bg-slate-900 p-0.5 border border-slate-800 gap-0.5">
                  {STATUS_OPTIONS.map((st) => (
                    <button
                      key={st}
                      type="button"
                      onClick={() => setStatus(st)}
                      className={`flex-1 py-1.5 px-2 text-[11px] font-semibold rounded-md transition text-center whitespace-nowrap ${
                        status === st ? 'bg-sky-600 text-white shadow-sm' : 'text-slate-400 hover:text-white'
                      }`}
                    >
                      {st}
                    </button>
                  ))}
                </div>
              </div>

              {/* Sorting Order Option */}
              <div>
                <label className="block text-[11px] font-semibold text-slate-400 mb-1 flex items-center space-x-1">
                  <ArrowUpDown className="w-3 h-3 text-sky-400" />
                  <span>Sorting Order</span>
                </label>
                <select
                  value={sortOption}
                  onChange={(e) => setSortOption(e.target.value)}
                  className="w-full px-2.5 py-1.5 bg-slate-900 border border-slate-800 rounded-lg text-white text-[11px] focus:outline-none focus:border-sky-500 transition cursor-pointer"
                >
                  {SORT_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Bottom Row: Category Option (Multi-Select Buttons) on dedicated line */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-[11px] font-semibold text-slate-400">
                  Categories <span className="text-slate-500 font-normal">(Multi-Select)</span>
                </label>
                <button
                  type="button"
                  onClick={selectAllCategories}
                  className={`text-[10px] px-2 py-0.5 rounded transition border ${
                    isAllSelected
                      ? 'bg-slate-800 text-sky-400 border-sky-500/40 font-semibold'
                      : 'bg-slate-900 text-slate-400 border-slate-800 hover:text-white'
                  }`}
                >
                  All Compounds
                </button>
              </div>
              <div className="grid grid-cols-3 gap-2">
                {CATEGORY_DEFS.map(({ id, label, icon: Icon, color }) => {
                  const isSelected = categories.includes(id);
                  return (
                    <button
                      key={id}
                      type="button"
                      onClick={() => toggleCategory(id)}
                      className={`py-1.5 px-3 rounded-lg border text-[11px] font-medium flex items-center justify-center space-x-1.5 transition ${
                        isSelected
                          ? color === 'sky'
                            ? 'bg-sky-500/15 border-sky-500/50 text-sky-300 shadow-sm'
                            : color === 'purple'
                            ? 'bg-purple-500/15 border-purple-500/50 text-purple-300 shadow-sm'
                            : 'bg-rose-500/15 border-rose-500/50 text-rose-300 shadow-sm'
                          : 'bg-slate-900 border-slate-800 text-slate-500 hover:text-slate-300'
                      }`}
                    >
                      <Icon className="w-3.5 h-3.5 shrink-0" />
                      <span>{label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

          </div>

          {/* Preview Format Controls Bar */}
          <div className="bg-slate-950/60 p-2.5 rounded-xl border border-slate-800 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold text-slate-300">Table Output Format</span>
              <div className="flex rounded-lg bg-slate-900 p-0.5 border border-slate-800">
                <button
                  type="button"
                  onClick={() => setFormat('markdown')}
                  className={`py-1 px-3 text-[11px] font-semibold rounded-md transition ${
                    format === 'markdown' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  Markdown (.md)
                </button>
                <button
                  type="button"
                  onClick={() => setFormat('text')}
                  className={`py-1 px-3 text-[11px] font-semibold rounded-md transition ${
                    format === 'text' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  Plain Text (.txt)
                </button>
                <button
                  type="button"
                  onClick={() => setFormat('discord')}
                  className={`py-1 px-3 text-[11px] font-semibold rounded-md transition ${
                    format === 'discord'
                      ? 'bg-[#5865F2] text-white'
                      : 'text-slate-400 hover:text-white'
                  }`}
                >
                  Discord Chat
                </button>
              </div>
            </div>
            {format === 'markdown' && (
              <p className="text-[11px] text-slate-500">
                Upload this .md file to Discord to see a rendered table preview.
              </p>
            )}
            {format === 'discord' && (
              <p className="text-[11px] text-slate-500">
                Uses a compact pipe-separated format (Name, Status, Dosage, Schedule, Type, Goal) so more records fit in Discord’s 2000-character limit. If your stack is still too long, it splits into numbered messages instead of omitting records.
              </p>
            )}
          </div>

          {/* Column Selector Controls */}
          {format !== 'discord' && (
          <div className="bg-slate-950/60 p-3.5 rounded-xl border border-slate-800 space-y-2.5">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <SlidersHorizontal className="w-3.5 h-3.5 text-sky-400" />
                <span className="text-[11px] font-semibold text-slate-200 uppercase tracking-wider">
                  Included Table Columns ({selectedColumns.length} / {AVAILABLE_TABLE_COLUMNS.length})
                </span>
              </div>
              <div className="flex items-center space-x-1.5 text-[10px]">
                <button
                  type="button"
                  onClick={() => handleSelectPreset('default')}
                  className="px-2 py-0.5 rounded bg-slate-900 hover:bg-slate-800 text-slate-300 border border-slate-800 transition"
                >
                  Default
                </button>
                <button
                  type="button"
                  onClick={() => handleSelectPreset('compact')}
                  className="px-2 py-0.5 rounded bg-slate-900 hover:bg-slate-800 text-slate-300 border border-slate-800 transition"
                >
                  Compact
                </button>
                <button
                  type="button"
                  onClick={() => handleSelectPreset('all')}
                  className="px-2 py-0.5 rounded bg-slate-900 hover:bg-slate-800 text-slate-300 border border-slate-800 transition"
                >
                  Select All
                </button>
              </div>
            </div>

            {/* Checkable Column Badges */}
            <div className="flex flex-wrap gap-1.5 pt-1">
              {AVAILABLE_TABLE_COLUMNS.map((col) => {
                const isSelected = selectedColumns.includes(col.id);
                return (
                  <button
                    key={col.id}
                    type="button"
                    onClick={() => toggleColumn(col.id)}
                    className={`inline-flex items-center space-x-1.5 px-2.5 py-1 rounded-lg text-[11px] font-medium transition cursor-pointer border ${
                      isSelected
                        ? 'bg-sky-500/15 text-sky-300 border-sky-500/40 shadow-sm'
                        : 'bg-slate-900 text-slate-500 border-slate-800 hover:text-slate-300'
                    }`}
                  >
                    {isSelected ? (
                      <CheckSquare className="w-3 h-3 text-sky-400 shrink-0" />
                    ) : (
                      <Square className="w-3 h-3 text-slate-600 shrink-0" />
                    )}
                    <span>{col.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
          )}

          {/* Copy Toast Banner */}
          {copiedFormat && (
            <div className="p-2.5 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-300 text-xs flex items-center space-x-2 animate-fadeIn">
              <Check className="w-4 h-4 text-emerald-400 flex-shrink-0" />
              <span className="font-medium">
                Copied{' '}
                {copiedFormat === 'markdown'
                  ? 'Markdown Table'
                  : copiedFormat === 'discord'
                  ? 'Discord Table'
                  : 'Plain Text Table'}{' '}
                to clipboard!
              </span>
            </div>
          )}

          {/* Code/Text Output Box */}
          <div className="relative group">
            <div className="absolute top-2 right-2 flex items-center space-x-1.5 opacity-90 transition">
              <button
                type="button"
                onClick={() => handleCopy(format)}
                className="p-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-300 border border-slate-700 text-[11px] flex items-center space-x-1 transition shadow-md"
                title={`Copy current ${format === 'markdown' ? 'Markdown' : format === 'discord' ? 'Discord' : 'Text'} preview`}
              >
                {copiedFormat === format ? (
                  <>
                    <Check className="w-3.5 h-3.5 text-emerald-400" />
                    <span className="text-emerald-400">Copied!</span>
                  </>
                ) : (
                  <>
                    <Copy className="w-3.5 h-3.5" />
                    <span>Copy Preview</span>
                  </>
                )}
              </button>
            </div>

            <pre className="w-full p-4 bg-slate-950 border border-slate-800 rounded-xl text-slate-300 font-mono text-[11px] leading-relaxed overflow-x-auto whitespace-pre select-all max-h-[40vh] sm:max-h-[45vh] overscroll-contain">
              {currentContent}
            </pre>
          </div>

        </div>

        {/* Sticky Footer Actions */}
        <div className="p-4 sm:p-5 border-t border-slate-800 flex flex-wrap items-center justify-between gap-3 shrink-0 bg-slate-900/95 backdrop-blur-sm">
          
          {/* Action buttons */}
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => handleCopy('markdown')}
              className="px-3.5 py-2 rounded-xl bg-sky-600 hover:bg-sky-500 text-white font-semibold text-xs transition flex items-center space-x-1.5 shadow-md shadow-sky-600/20"
            >
              {copiedFormat === 'markdown' ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              <span>Copy as Markdown</span>
            </button>

            <button
              type="button"
              onClick={() => handleCopy('text')}
              className="px-3.5 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white font-semibold text-xs transition flex items-center space-x-1.5 shadow-md shadow-indigo-600/20"
            >
              {copiedFormat === 'text' ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
              <span>Copy as Plain Text</span>
            </button>

            <button
              type="button"
              onClick={() => setIsDiscordCopyModalOpen(true)}
              className="px-3.5 py-2 rounded-xl bg-[#5865F2] hover:bg-[#4752C4] text-white font-semibold text-xs transition flex items-center space-x-1.5 shadow-md shadow-[#5865F2]/20"
            >
              <MessageCircle className="w-3.5 h-3.5" />
              <span>Copy for Discord</span>
            </button>

            <button
              type="button"
              onClick={handleDownload}
              className="px-3.5 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-200 font-semibold text-xs transition flex items-center space-x-1.5 border border-slate-700"
            >
              <Download className="w-3.5 h-3.5" />
              <span>
                Download .{format === 'markdown' ? 'md' : format === 'discord' ? 'txt' : 'txt'}
              </span>
            </button>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 font-medium transition text-xs ml-auto"
          >
            Close
          </button>

        </div>

      </div>
    </div>

    <DiscordCopyModal
      isOpen={isDiscordCopyModalOpen}
      onClose={() => setIsDiscordCopyModalOpen(false)}
      protocols={protocols}
      options={{ status, categories, sortOption }}
    />
    </>
  );
}
